import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
} from "@angular/core";

import { AuthService } from "#root/core/auth/auth.service";
import {
  filter,
  fromEvent,
  interval,
  merge,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
} from "rxjs";

const COUNTDOWN_SECONDS = 600;
const YELLOW_THRESHOLD = 300;
const RED_THRESHOLD = 60;
const ACTIVITY_RESUME_DELAY_MS = 300;
const BACKSTOP_CHECK_MS = 15 * 60 * 1000;
const DEPLOY_CHANNEL_NAME = "deploy-version";
const VERSION_AVAILABLE_MESSAGE = "version-available";
const UPDATING_MESSAGE = "updating";

type DeployBannerPhase = "green" | "yellow" | "red";

interface DeployBannerPhaseStyle {
  icon: string;
  title: string;
  lead: string;
  container: string;
  text: string;
  accent: string;
  button: string;
  progress: string;
}

const PHASE_STYLES: Record<DeployBannerPhase, DeployBannerPhaseStyle> = {
  green: {
    icon: "system_update_alt",
    title: "Nueva versión disponible.",
    lead: " Guarde su trabajo — la sesión se cerrará en ",
    container:
      "bg-green-500/20 border-green-500 dark:bg-green-400/10 dark:border-green-400/60",
    text: "text-green-900 dark:text-green-200",
    accent: "text-green-700 dark:text-green-400",
    button:
      "border-green-700 text-green-800 dark:border-green-400 dark:text-green-300",
    progress: "bg-green-600 dark:bg-green-400",
  },
  yellow: {
    icon: "warning_amber",
    title: "La sesión se cerrará pronto.",
    lead: " Guarde su trabajo — cierre en ",
    container:
      "bg-amber-500/20 border-amber-500 dark:bg-amber-400/10 dark:border-amber-400/60",
    text: "text-amber-900 dark:text-amber-200",
    accent: "text-amber-700 dark:text-amber-400",
    button:
      "border-amber-700 text-amber-800 dark:border-amber-400 dark:text-amber-300",
    progress: "bg-amber-600 dark:bg-amber-400",
  },
  red: {
    icon: "error_outline",
    title: "Cierre de sesión inminente.",
    lead: " Se cerrará en ",
    container:
      "bg-red-500/20 border-red-500 dark:bg-red-400/10 dark:border-red-400/60",
    text: "text-red-900 dark:text-red-200",
    accent: "text-red-700 dark:text-red-400",
    button:
      "border-red-700 text-red-800 dark:border-red-400 dark:text-red-300",
    progress: "bg-red-600 dark:bg-red-400",
  },
};

@Component({
  selector: "deploy-banner",
  templateUrl: "./deploy-banner.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeployBannerComponent implements OnInit, OnDestroy {
  public visible: boolean = false;
  public remaining: number = COUNTDOWN_SECONDS;
  public paused: boolean = false;

  private _interval: ReturnType<typeof setInterval> | null = null;
  private _channel: BroadcastChannel | null = null;
  private _deadline: number = 0;
  private _lastTick: number = 0;
  private readonly _unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    private readonly authService: AuthService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly ngZone: NgZone,
  ) {}

  get phase(): DeployBannerPhase {
    if (this.remaining <= RED_THRESHOLD) return "red";
    if (this.remaining <= YELLOW_THRESHOLD) return "yellow";
    return "green";
  }

  get phaseStyle(): DeployBannerPhaseStyle {
    return PHASE_STYLES[this.phase];
  }

  get progressPct(): number {
    return (this.remaining / COUNTDOWN_SECONDS) * 100;
  }

  get formattedTime(): string {
    const minutes: number = Math.floor(this.remaining / 60);
    const seconds: number = this.remaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  ngOnInit(): void {
    this.openChannel();

    this.authService.newVersionAvailable$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((available: boolean) => {
        if (!available) return;

        this._channel?.postMessage(VERSION_AVAILABLE_MESSAGE);
        this.show();
      });

    merge(fromEvent(document, "visibilitychange"), interval(BACKSTOP_CHECK_MS))
      .pipe(
        filter(() => !this.visible && document.visibilityState === "visible"),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe(() => this.authService.checkDeployVersion());
  }

  ngOnDestroy(): void {
    this.stopCountdown();
    this._channel?.close();
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  update(): void {
    this._channel?.postMessage(UPDATING_MESSAGE);

    this.authService
      .signOut()
      .pipe(take(1))
      .subscribe(() => this.reloadOnSignIn());
  }

  @HostListener("window:beforeunload")
  onBeforeUnload(): void {
    if (this.visible) this.authService.clearStorageForNewVersion();
  }

  private reloadOnSignIn(): void {
    this.authService.clearStorageForNewVersion();
    window.location.hash = "#/sign-in";
    window.location.reload();
  }

  private openChannel(): void {
    if (typeof BroadcastChannel === "undefined") return;

    this._channel = new BroadcastChannel(DEPLOY_CHANNEL_NAME);
    this._channel.onmessage = (event: MessageEvent) => {
      this.ngZone.run(() => {
        if (event.data === UPDATING_MESSAGE) {
          window.location.reload();
          return;
        }

        if (event.data === VERSION_AVAILABLE_MESSAGE) this.show();
      });
    };
  }

  private show(): void {
    if (this.visible) return;

    this.visible = true;
    this.startCountdown();
    this.listenForUserActivity();
    this.changeDetectorRef.markForCheck();
  }

  private startCountdown(): void {
    this._deadline = Date.now() + COUNTDOWN_SECONDS * 1000;
    this._lastTick = Date.now();

    this.ngZone.runOutsideAngular(() => {
      this._interval = setInterval(() => this.tick(), 1000);
    });
  }

  private tick(): void {
    const now: number = Date.now();
    if (this.paused) this._deadline += now - this._lastTick;
    this._lastTick = now;

    const remaining: number = Math.max(
      0,
      Math.round((this._deadline - now) / 1000),
    );
    if (remaining === this.remaining) return;

    this.remaining = remaining;
    this.changeDetectorRef.detectChanges();

    if (this.remaining <= 0) {
      this.stopCountdown();
      this.ngZone.run(() => this.update());
    }
  }

  private stopCountdown(): void {
    if (this._interval === null) return;

    clearInterval(this._interval);
    this._interval = null;
  }

  private listenForUserActivity(): void {
    merge(fromEvent(document, "keydown"), fromEvent(document, "input"))
      .pipe(
        tap(() => this.setPaused(true)),
        switchMap(() => timer(ACTIVITY_RESUME_DELAY_MS)),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe(() => this.setPaused(false));
  }

  private setPaused(paused: boolean): void {
    if (this.paused === paused) return;

    this.paused = paused;
    this.changeDetectorRef.markForCheck();
  }
}
